// 012A — Imagenología Solicitud (Anexo 1 MSP, SNS-MSP/HCU-form.012A/2021). Fase 7, Tanda 3: bloques A-G del papel
// (servicio y prioridad, estudio solicitado con casillas y sedación, motivo con FUM y paciente contaminado, resumen
// clínico, 6 diagnósticos con CIE y PRE/DEF). Campos y decisiones en `bloquesImagenologiaDatos.ts`. El bloque A lo
// resuelve `EncabezadoMspCampos`; el profesional (G) lo arma el servidor en el PDF firmado.
import { useMedplum } from '@medplum/react-hooks';
import type { ServiceRequest } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { AreaDictado } from './AreaDictado';
import { CATEGORIA_IMAGENOLOGIA } from './categoriasDiagnosticas';
import { Bloque, CamposSimples } from './camposSimples';
import { MARCAS_PRE_DEF } from './camposSimplesDatos';
import { SelectorDiagnosticoConMarca } from './MarcaDiagnostico';
import { codeDeDiagnostico } from './SelectorDiagnosticoCie10';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import {
  CAMPOS_ESTUDIO_IMAGEN,
  CAMPOS_MOTIVO_012A,
  CAMPOS_RESUMEN_012A,
  CAMPOS_SERVICIO_012A,
  estadoInicial012A,
  paresDe012A,
  prioridadFhir012A,
  textoSolicitud012A,
  type Estado012A,
} from './bloquesImagenologiaDatos';

const CAMPOS_MOTIVO_SIN_AREA = CAMPOS_MOTIVO_012A.filter((c) => c.tipo !== 'area');
const CAMPO_MOTIVO = CAMPOS_MOTIVO_012A.find((c) => c.tipo === 'area')!;
const CAMPOS_ESTUDIO_SIN_DESCRIPCION = CAMPOS_ESTUDIO_IMAGEN.filter((c) => c.clave !== 'descripcion');
const CAMPO_DESCRIPCION = CAMPOS_ESTUDIO_IMAGEN.find((c) => c.clave === 'descripcion')!;

export function Formulario012A({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [estado, setEstado] = useState<Estado012A>(() => estadoInicial012A());
  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const { guardando, error, guardar } = useGuardado();
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, '012A', t('formularios.f012a.titulo'), { servicio: estado.servicio.servicio });

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const reasonCode = estado.diagnosticos.filter((d) => d.diagnostico.texto.trim()).map((d) => codeDeDiagnostico(d.diagnostico));
      const resumen = (estado.resumen.resumenClinico ?? '').trim();
      const descripcion = (estado.estudio.descripcion ?? '').trim();
      await medplum.createResource<ServiceRequest>({
        resourceType: 'ServiceRequest',
        status: 'active',
        intent: 'order',
        category: [CATEGORIA_IMAGENOLOGIA],
        priority: prioridadFhir012A(estado.servicio.prioridad),
        code: { text: textoSolicitud012A(estado) },
        subject,
        encounter,
        authoredOn: new Date().toISOString(),
        reasonCode: reasonCode.length ? reasonCode : undefined,
        note: [...(descripcion ? [{ text: `Descripción: ${descripcion}` }] : []), ...(resumen ? [{ text: `Resumen clínico: ${resumen}` }] : [])],
      });
      onGuardado(paresDe012A(estado), datosEncabezadoMsp);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{t('formularios.f012a.titulo')}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <Bloque titulo={t('formularios.f012a.servicioTitulo')}>
        <CamposSimples prefijoId="f012a-srv" campos={CAMPOS_SERVICIO_012A} valores={estado.servicio} columnas={3} onChange={(v) => setEstado({ ...estado, servicio: v })} />
      </Bloque>

      <Bloque titulo={t('formularios.f012a.estudioTitulo')}>
        <CamposSimples prefijoId="f012a-est" campos={CAMPOS_ESTUDIO_SIN_DESCRIPCION} valores={estado.estudio} columnas={4} onChange={(v) => setEstado({ ...estado, estudio: v })} />
        <AreaDictado id="f012a-est-descripcion" etiqueta={CAMPO_DESCRIPCION.etiqueta} valor={estado.estudio.descripcion ?? ''} filas={3} onChange={(v) => setEstado({ ...estado, estudio: { ...estado.estudio, descripcion: v } })} />
      </Bloque>

      <Bloque titulo={t('formularios.f012a.motivoTitulo')}>
        <CamposSimples prefijoId="f012a-mot" campos={CAMPOS_MOTIVO_SIN_AREA} valores={estado.motivo} columnas={3} onChange={(v) => setEstado({ ...estado, motivo: { ...estado.motivo, ...v } })} />
        <AreaDictado id="f012a-mot-motivo" etiqueta={CAMPO_MOTIVO.etiqueta} valor={estado.motivo.motivo ?? ''} filas={3} onChange={(v) => setEstado({ ...estado, motivo: { ...estado.motivo, motivo: v } })} />
      </Bloque>

      <Bloque titulo={t('formularios.f012a.resumenTitulo')}>
        <AreaDictado id="f012a-resumen" etiqueta={CAMPOS_RESUMEN_012A[0]!.etiqueta} valor={estado.resumen.resumenClinico ?? ''} filas={4} onChange={(v) => setEstado({ ...estado, resumen: { resumenClinico: v } })} />
      </Bloque>

      <Bloque titulo={t('formularios.f012a.diagnosticoTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f012a.diagnosticoNota')}</p>
        {estado.diagnosticos.map((d, i) => (
          <SelectorDiagnosticoConMarca
            key={i}
            label={`${t('formularios.f012a.diagnostico')} ${i + 1}`}
            marcaLabel={t('formularios.f012a.marcaDiagnostico')}
            marcas={MARCAS_PRE_DEF}
            value={d}
            onChange={(v) => setEstado({ ...estado, diagnosticos: estado.diagnosticos.map((x, j) => (j === i ? v : x)) })}
          />
        ))}
      </Bloque>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
